"use client";

import { rashis } from "@ishvaram/domain-data/rashis";

import type { ActiveTransit, SadeSatiInfo } from "@/lib/api/transits";
import {
  SATURN_STATUS_STYLES,
  dhaiyaBadge,
  houseOrdinal,
  type DhaiyaBadge,
} from "@/lib/saturn-status";

type SaturnStatusStyle = (typeof SATURN_STATUS_STYLES)[keyof typeof SATURN_STATUS_STYLES];

export function RashiPicker({
  selectedRashi,
  onSelect,
  sadeSati,
  saturnTransit,
  selectedSaturn,
  isLoading,
  language,
}: {
  selectedRashi: string | null;
  onSelect: (id: string | null) => void;
  sadeSati: SadeSatiInfo | null | undefined;
  saturnTransit: ActiveTransit | null;
  /** Saturn's entry in the engine reply for the picked Moon sign, once that reply is in. */
  selectedSaturn?: ActiveTransit;
  isLoading: boolean;
  language: "en" | "hi";
}) {
  const hi = language === "hi";

  return (
    <div className="card-sacred mb-6 p-5 sm:p-6">
      <p className="mb-4 text-center text-body-small font-semibold text-ish-dark">
        {hi ? "अपनी जन्म राशि चुनें" : "Select your Moon sign to check live"}
      </p>
      <RashiButtons selectedRashi={selectedRashi} onSelect={onSelect} hi={hi} />
      <SelectedRashiResult
        selectedRashi={selectedRashi}
        sadeSati={sadeSati}
        saturnTransit={saturnTransit}
        selectedSaturn={selectedSaturn}
        isLoading={isLoading}
        hi={hi}
      />
    </div>
  );
}

function RashiButtons({
  selectedRashi,
  onSelect,
  hi,
}: {
  selectedRashi: string | null;
  onSelect: (id: string | null) => void;
  hi: boolean;
}) {
  return (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
      {rashis.map((rashi) => {
        const isSelected = selectedRashi === rashi.id;
        return (
          <button
            key={rashi.id}
            type="button"
            onClick={() => onSelect(isSelected ? null : rashi.id)}
            className="flex flex-col items-center gap-0.5 rounded-lg border p-2 text-center transition-all duration-150"
            style={{
              borderColor: isSelected ? "var(--ish-accent)" : "var(--ish-border-light)",
              backgroundColor: isSelected ? "var(--ish-accent-light)" : "transparent",
            }}
          >
            <span className="text-body-small font-bold text-ish-dark">
              {hi ? rashi.name_hi : rashi.name_en}
            </span>
            {hi ? null : <span className="text-[10px] text-ish-text-faint">{rashi.name_hi}</span>}
          </button>
        );
      })}
    </div>
  );
}

function SelectedRashiResult({
  selectedRashi,
  sadeSati,
  saturnTransit,
  selectedSaturn,
  isLoading,
  hi,
}: {
  selectedRashi: string | null;
  sadeSati: SadeSatiInfo | null | undefined;
  saturnTransit: ActiveTransit | null;
  selectedSaturn?: ActiveTransit;
  isLoading: boolean;
  hi: boolean;
}) {
  const selectedData = rashis.find((rashi) => rashi.id === selectedRashi);
  if (!selectedData) return null;

  const pending = isLoading || sadeSati === undefined;
  const dhaiya = pending || sadeSati ? null : dhaiyaBadge(selectedSaturn);
  const styles = SATURN_STATUS_STYLES[sadeSati?.phase ?? (dhaiya ? "dhaiya" : "none")];
  return (
    <div
      className="mt-4 rounded-lg p-4"
      {...(dhaiya ? { "data-saturn-status": "dhaiya" } : {})}
      style={{ backgroundColor: styles.bg, border: `1px solid ${styles.border}` }}
    >
      {pending ? (
        <p className="text-body-small text-ish-text-muted">
          {hi ? "शनि का लाइव गोचर जाँचा जा रहा है…" : "Checking the live Saturn transit…"}
        </p>
      ) : (
        <ResolvedRashiStatus
          nameEn={selectedData.name_en}
          nameHi={selectedData.name_hi}
          sadeSati={sadeSati}
          saturnTransit={saturnTransit}
          dhaiya={dhaiya}
          styles={styles}
          hi={hi}
        />
      )}
    </div>
  );
}

function ResolvedRashiStatus({
  nameEn,
  nameHi,
  sadeSati,
  saturnTransit,
  dhaiya,
  styles,
  hi,
}: {
  nameEn: string;
  nameHi: string;
  sadeSati: SadeSatiInfo | null;
  saturnTransit: ActiveTransit | null;
  dhaiya: DhaiyaBadge | null;
  styles: SaturnStatusStyle;
  hi: boolean;
}) {
  // The short Dhaiya badge keeps the row inside the slot the phase badges
  // already use (see DHAIYA_LABELS in saturn-status.ts for the measurement).
  const badge = sadeSati
    ? `${sadeSati.phase.toUpperCase()} PHASE`
    : (dhaiya?.short ?? "NOT ACTIVE");
  const description = getStatusDescription(sadeSati, saturnTransit, dhaiya, hi);
  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-[16px] font-bold" style={{ color: styles.text }}>
          {nameEn} ({nameHi})
        </span>
        <span
          className="rounded px-2.5 py-1 text-[11px] font-semibold"
          style={{ backgroundColor: styles.badgeBg, color: styles.badge }}
        >
          {badge}
        </span>
      </div>
      <p className="text-body-small" style={{ color: styles.text }}>
        {description}
      </p>
    </>
  );
}

function getStatusDescription(
  sadeSati: SadeSatiInfo | null,
  saturnTransit: ActiveTransit | null,
  dhaiya: DhaiyaBadge | null,
  hi: boolean
) {
  if (sadeSati && hi) {
    const phase = sadeSati.phase_description_hi || sadeSati.phase_description_en;
    return `${phase} यह चरण शनि की लाइव गणना की गई स्थिति से निकला है।`;
  }
  if (sadeSati) return sadeSati.phase_description_en;

  const saturnSign = saturnTransit?.current_rashi_name_en;
  const saturnSignHi = saturnTransit?.current_rashi_name_hi ?? "वर्तमान राशि";
  // The Dhaiya lines are never taller than the engine's phase lines in the same
  // language (measured 2026-10-21 at 320-640 px), so the panel stays inside
  // the height a Sade Sati result already takes.
  if (dhaiya) {
    const house = houseOrdinal(dhaiya.house);
    if (hi) {
      const name = dhaiya.house === 4 ? "कंटक शनि" : "अष्टम शनि";
      return `साढ़े साती सक्रिय नहीं है। शनि ${saturnSignHi} में इस जन्म राशि से ${dhaiya.house}वें भाव में है, इसलिए यह ढैया (${name}) है।`;
    }
    return `Sade Sati is not active. Shani in ${saturnSign ?? "its current sign"} is ${house} from this Moon sign: Dhaiya (${dhaiya.name}).`;
  }
  if (hi) {
    return `शनि ${saturnSignHi} में है; इस जन्म राशि के लिए साढ़े साती सक्रिय नहीं है।`;
  }
  return `Shani in ${saturnSign ?? "its current sign"} is not in the 12th, 1st, or 2nd sign from this Moon sign.`;
}
